(ns app.front.view.admin.tenant-management.products-tab
  "Products tab of the tenant detail view: every primary permit type the platform
  offers, marked enabled or not, with the tenant's authority group for it. Each
  row expands to enable or disable the product, pick its default categories and
  rename its authority group. With `:feature-flag.id/external-modules` on, each
  external product (PWAM) gets a row too, whose expansion sets the renewal
  date and departments; see
  `app.front.view.admin.tenant-management.external-module-subs`."
  (:require [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.external-module.model :as external-model]
            [app.front.ui :as ui]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.ui.v2.status-tag :as ui-status-tag]
            [app.front.util :refer [for-frag]]
            [app.front.view.admin.domain-groups.municipality-categories :as municipality-categories]
            [app.front.view.admin.domain-groups.municipality-groups :as mg]
            [app.front.view.admin.tenant-management.common :as tm-common]
            [app.front.view.admin.tenant-management.external-module-subs :as em-subs]
            [app.front.view.admin.tenant-management.subs :as tm-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

;; ---------------------------------------------------------------------------
;; Enabled categories per product, from `:admin/group-configs` as in the
;; municipality-groups editor: a category is enabled unless its config is hidden.
;; ---------------------------------------------------------------------------

(defn- enabled-categories [group-configs]
  (->> group-configs
       (filter #(-> % :group.config/level (= :group-config.level/permit-type)))
       (reduce (fn [acc root]
                 (assoc acc
                        (:group.config/target root)
                        (->> group-configs
                             (filter #(and (-> % :entity/parent (= (:db/id root)))
                                           (-> % :domain/state
                                               (not= :group-config.state/hidden))))
                             (keep :group.config/target)
                             set)))
               {})))

(rf/reg-event-fx ::fetch-product-configs
  (fn [_ [_ group-code]]
    {:comm/command {:command    :admin/group-configs
                    :method     :get
                    :data       {:domain-group-id group-code}
                    :on-success [::fetch-product-configs.success group-code]}}))

(rf/reg-event-db ::fetch-product-configs.success
  (fn [db [_ group-code _ group-configs]]
    (assoc-in db [::enabled-categories group-code] (enabled-categories group-configs))))

;; Keyed by tenant, so another tenant's categories never show while a fetch runs.
(rf/reg-sub ::enabled-categories
  (fn [db [_ group-code]]
    (get-in db [::enabled-categories group-code])))

(rf/reg-event-fx ::set-permit-type
  (fn [_ [_ {:keys [tenant-id group-code permit-type enabled? categories]}]]
    {:comm/command {:command          :admin/tenant-set-permit-type
                    :data             (p/assoc-if {:domain-group-id tenant-id
                                                   :permit-type     permit-type
                                                   :enabled?        enabled?}
                                                  :categories (not-empty categories))
                    :notify           {:success :changes-saved}
                    ;; Enabling creates an authority group, so re-pull the tenant
                    ;; tree rather than trying to patch the entity-db.
                    :finally-dispatch [::refresh tenant-id group-code]}}))

(rf/reg-event-fx ::refresh
  (fn [_ [_ tenant-id group-code]]
    ;; Enabling a product creates an authority group, so re-pull this tenant
    ;; rather than trying to patch the entity-db.
    {:dispatch-n [[::tm-subs/fetch-tenant tenant-id]
                  [::fetch-product-configs group-code]]}))

;; ---------------------------------------------------------------------------
;; Deriving a row per product
;; ---------------------------------------------------------------------------

(defn- authority-group-by-permit-type
  "Each enabled permit type's authority group: the one owning its non-hidden
  permit-type-config, as the backend resolves it."
  [authority-groups]
  (->> authority-groups
       (mapcat (fn [ag]
                 (->> (group-model/permit-type-configs ag)
                      (remove :permit-type-config/hidden?)
                      (keep (fn [ptc]
                              (when-let [pt (:permit-type-config/permit-type ptc)]
                                [pt ag]))))))
       (into {})))

(defn product-rows
  "One row per product in the platform's permit-type catalogue, in translated
  name order."
  [tr {:keys [tenant-id group-code permit-type->categories authority-groups]}]
  (let [ag-by-pt (authority-group-by-permit-type authority-groups)]
    (->> (keys permit-type->categories)
         tm-common/products
         (map (fn [pt]
                (let [ag (get ag-by-pt pt)]
                  {:id                   (str pt)
                   :permit-type          pt
                   :name                 (tm-common/product-name tr pt)
                   :enabled?             (some? ag)
                   :authority-group      ag
                   :tenant-id            tenant-id
                   :group-code           group-code
                   :available-categories (->> (get permit-type->categories pt)
                                              (sort-by (comp tr vector))
                                              vec)})))
         (sort-by :name)
         vec)))

(defn external-module-rows
  "One row per external system the installation can license products from,
  with the tenant's module group for it when the product has been enabled.
  `:authority-group` is the module group, so the shared columns read it."
  [{:keys [tenant-id group-code catalogue domain-group]}]
  (->> catalogue
       (map (fn [{:keys [system module-id module-name] :as module}]
              (let [module-group (external-model/module-group-of domain-group system module-id)]
                {:id              (str "external-" (name system))
                 :external-module module
                 :name            module-name
                 :enabled?        (boolean (and module-group (external-model/enabled? module-group)))
                 :authority-group module-group
                 :tenant-id       tenant-id
                 :group-code      group-code})))
       (sort-by :name)
       vec))

;; ---------------------------------------------------------------------------
;; Row expansion
;; ---------------------------------------------------------------------------

(defn category-checkboxes
  "The product's default categories. `enabled?` false greys them out, since
  categories only mean anything for a product the tenant actually has."
  [{:keys [tr permit-type available-categories selected enabled? on-toggle]}]
  (when (seq available-categories)
    [:div.mt-4
     [:h5.mb-2 (tr [:admin/municipality-categories])]
     [:div.grid.gap-x-4 {:class "sm:grid-cols-2 lg:grid-cols-3"}
      (for-frag [category available-categories]
        [:div {:key category}
         (cc/checkbox {:id         (str "tm-cat-" (name permit-type) "-" (name category))
                       :label-text (tr [category])
                       :checked    (contains? selected category)
                       ;; Categories only mean anything for an enabled product.
                       :disabled   (not enabled?)
                       :on-change  #(on-toggle category %)})])]]))

(defn- product-editor
  "Enable or disable the product, choose its default categories and rename its
  authority group; saving sends only the changed parts. The draft holds only
  touched fields, so values still loading (the categories) show once they
  arrive."
  [tr {:keys [tenant-id group-code permit-type enabled? authority-group available-categories]}]
  (r/with-let [*draft  (r/atom {})
               ;; Bumped on cancel to remount the uncontrolled name field.
               *resets (r/atom 0)
               revert! (fn []
                         (reset! *draft {})
                         (swap! *resets inc))]
    (let [current-cats   (or (get (<sub [::enabled-categories group-code]) permit-type) #{})
          current-name   (:group/name authority-group)
          draft          @*draft
          draft-enabled? (get draft :enabled? enabled?)
          draft-cats     (get draft :categories current-cats)
          new-name       (str/trim (or (get draft :name current-name) ""))
          name-dirty?    (and authority-group
                              (not (str/blank? new-name))
                              (not= current-name new-name))
          config-dirty?  (or (not= enabled? draft-enabled?)
                             (and draft-enabled? (not= current-cats draft-cats)))
          dirty?         (or name-dirty? config-dirty?)]
      [:div.p-2
       (cc/checkbox {:id         (str "tm-product-enabled-" (name permit-type))
                     :label-text (tr [:admin/tenant-product-enabled])
                     :checked    (boolean draft-enabled?)
                     :on-change  (fn [checked?]
                                   (swap! *draft assoc :enabled? checked?)
                                   ;; Pick the platform defaults the first time a
                                   ;; product is enabled, like the group editor.
                                   (when (and checked? (empty? draft-cats))
                                     (let [defaults (set (ptc-model/filter-default-category-seq
                                                           permit-type
                                                           available-categories))]
                                       (swap! *draft assoc :categories defaults))))})
       (when (and (not enabled?) draft-enabled?)
         (ui/help (tr [:admin/tenant-product-enable-help])))
       (when (and enabled? (not draft-enabled?))
         (ui/warning (tr [:admin/tenant-product-disable-warning])))

       [category-checkboxes {:tr                   tr
                             :permit-type          permit-type
                             :available-categories available-categories
                             :selected             draft-cats
                             :enabled?             draft-enabled?
                             :on-toggle            (fn [category checked?]
                                                     (let [categories ((if checked? conj disj) draft-cats category)]
                                                       (swap! *draft assoc :categories categories)))}]

       (when authority-group
         [:div.mt-4
          [:h5.mb-2 (tr [:admin/tenant-authority-group])]
          [:div.flex.items-end.gap-3.flex-wrap
           [:div.flex-1.min-w-64
            ^{:key @*resets}
            [ui/text-input {:id            (str "tm-ag-name-" (name permit-type))
                            :label-text    (tr [:admin/group-name])
                            :default-value current-name
                            :on-change     #(swap! *draft assoc :name (-> % .-target .-value))}]]]
          [:p.text-sm.text-gray-600.mt-2
           (tr [:admin/tenant-authority-group-id] [(:group/id authority-group)])]])

       [:div.flex.justify-end.gap-2.mt-6
        [ui/button {:kind     :secondary
                    :size     :small
                    :disabled (not dirty?)
                    :on-click revert!}
         (tr [:cancel])]
        [ui/button {:kind     :primary
                    :size     :small
                    :disabled (not dirty?)
                    :on-click (fn []
                                (when name-dirty?
                                  (>evt [::mg/rename-group (:db/id authority-group) new-name]))
                                (when config-dirty?
                                  (>evt [::set-permit-type
                                         {:tenant-id   tenant-id
                                          :group-code  group-code
                                          :permit-type permit-type
                                          :enabled?    draft-enabled?
                                          :categories  draft-cats}]))
                                (revert!))}
         (tr [:save])]]])))

;; ---------------------------------------------------------------------------
;; Row expansion of an external product
;; ---------------------------------------------------------------------------

(defn- department-checkboxes
  "The tenant's departments in the other system. A department that exists has
  been sent there and stays checked; the rest of the system's default list and
  anything the admin adds are offered unchecked."
  [{:keys [tr system id-prefix module-name existing offered selected enabled? on-toggle on-add]}]
  (r/with-let [*custom (r/atom "")]
    (let [custom  (external-model/normalize-department-name @*custom)
          listed  (set (map external-model/department-slug (concat existing offered)))
          ;; The name becomes a code the other system keys the department by,
          ;; so say before saving when it cannot.
          problem (when-not (str/blank? custom)
                    (or (external-model/department-name-problem system custom)
                        (when (listed (external-model/department-slug custom)) :listed)))]
      [:div.mt-4
       [:h5.mb-1 (tr [:admin/tenant-departments])]
       [:p.text-sm.text-gray-600.mb-2 (tr [:admin/tenant-departments-help] [module-name])]
       [:div.grid.gap-x-4 {:class "sm:grid-cols-2 lg:grid-cols-3"}
        (for-frag [department (concat existing offered)]
          [:div {:key department}
           (cc/checkbox {:id         (str id-prefix "-dept-" (external-model/department-slug department))
                         :label-text department
                         :checked    (contains? selected department)
                         ;; Sent once, kept forever: the other system may have
                         ;; attributed data to it.
                         :disabled   (or (not enabled?) (contains? (set existing) department))
                         :on-change  #(on-toggle department %)})])]
       ;; Same grid as the checkboxes, so the input is one column wide.
       [:div.grid.gap-x-4.mt-2 {:class "sm:grid-cols-2 lg:grid-cols-3"}
        [:div.flex.items-end.gap-3
         [:div.flex-1
          [ui/text-input {:id         (str id-prefix "-dept-custom")
                          :label-text (tr [:admin/tenant-department-custom])
                          :value      @*custom
                          :disabled   (not enabled?)
                          :invalid    (some? problem)
                          :on-change  #(reset! *custom (-> % .-target .-value))}]]
         [ui/button {:kind     :secondary
                     :size     :field
                     :icon     i/plus
                     :disabled (or (not enabled?) (str/blank? custom) (some? problem))
                     :on-click (fn []
                                 (on-add custom)
                                 (reset! *custom ""))}
          (tr [:admin/tenant-department-add])]]]
       ;; Below the row rather than inside the input, so the button stays level with it.
       (when problem
         [:p.text-sm.text-ui-red-60.mt-1
          (case problem
            :invalid  (tr [:admin/tenant-department-invalid])
            :too-long (tr [:admin/tenant-department-too-long]
                          [(get external-model/department-max-length system)])
            :listed   (tr [:admin/tenant-department-listed]))])])))

(defn- external-module-editor
  "Enable or disable the product in the other system, set its renewal date and
  the tenant's departments. Saving sends only the commands whose part changed,
  and the row stays busy until the tenant is fetched again; groups still
  waiting for their push are listed, with a retry."
  [tr {:keys [tenant-id enabled? authority-group external-module]}]
  (let [{:keys [system module-name default-departments]} external-module
        row-key                                          [tenant-id system]
        edit!                                            #(rf/dispatch-sync [::em-subs/edit row-key %])
        saving?                                          (<sub [::em-subs/saving? row-key])
        module-group                                     authority-group
        id-prefix                                        (str "tm-external-" (name system))
        current-date                                     (:group/external-renewal-date module-group)
        existing                                         (->> (external-model/department-groups module-group)
                                                              (map :group/name)
                                                              vec)
        pending                                          (->> (group-model/child-groups module-group)
                                                              (filter external-model/pending?)
                                                              (map :group/name))
        draft                                            (<sub [::em-subs/draft row-key])
        draft-enabled?                                   (get draft :enabled? enabled?)
        draft-date                                       (get draft :renewal-date current-date)
        draft-departments                                (get draft :departments (set existing))
        offered                                          (->> (concat default-departments (:added draft))
                                                              (remove (set existing))
                                                              distinct)
        module-dirty?                                    (or (not= enabled? draft-enabled?)
                                                             (and draft-enabled? (not= current-date draft-date)))
        departments-dirty?                               (not= (set existing) draft-departments)
        valid?                                           (or (not draft-enabled?) (some? draft-date))
        dirty?                                           (or module-dirty? departments-dirty?)]
    [:div.p-2
     (cc/checkbox {:id         (str id-prefix "-enabled")
                   :label-text (tr [:admin/tenant-product-enabled])
                   :checked    (boolean draft-enabled?)
                   :disabled   saving?
                   :on-change  #(edit! {:enabled? %})})
     (when (and (not enabled?) draft-enabled?)
       (ui/help (tr [:admin/tenant-external-module-enable-help] [module-name])))
     (when (and enabled? (not draft-enabled?))
       (ui/warning (tr [:admin/tenant-external-module-disable-warning] [module-name])))

     [:div.grid.gap-4.mt-4 {:class "sm:grid-cols-3"}
      [ui/date-picker {:tr         tr
                       :id         (str id-prefix "-renewal-date")
                       :label-text (tr [:admin/tenant-renewal-date])
                       :value      draft-date
                       :disabled?  (or saving? (not draft-enabled?))
                       :required?  draft-enabled?
                       :invalid?   (and draft-enabled? (nil? draft-date))
                       :callback   #(edit! {:renewal-date %})}]]
     (when-let [tenant-id' (:group/external-tenant-id module-group)]
       [:p.text-sm.text-gray-600.mt-2
        (tr [:admin/tenant-external-tenant-id] [module-name tenant-id'])])

     (when (seq pending)
       [:div.flex.items-center.gap-3.flex-wrap
        (ui/warning {:class "flex-1"} (tr [:admin/tenant-sync-pending] [(str/join ", " pending) module-name]))
        [ui/button {:kind     :secondary
                    :size     :small
                    :disabled saving?
                    :on-click #(>evt [::em-subs/retry-sync {:tenant-id tenant-id :system system}])}
         (tr [:admin/tenant-retry-sync])]])

     (when module-group
       [department-checkboxes {:tr          tr
                               :system      system
                               :id-prefix   id-prefix
                               :module-name module-name
                               :existing    existing
                               :offered     offered
                               :selected    draft-departments
                               :enabled?    (and enabled? draft-enabled? (not saving?))
                               :on-toggle   (fn [department checked?]
                                              (edit! {:departments ((if checked? conj disj)
                                                                    draft-departments
                                                                    department)}))
                               :on-add      (fn [department]
                                              (edit! {:added       (conj (vec (:added draft)) department)
                                                      :departments (conj draft-departments department)}))}])

     [:div.flex.justify-end.items-center.gap-2.mt-6
      (when saving?
        (cc/inline-loading {:description (tr [:admin/tenant-external-module-saving] [module-name])}))
      [ui/button {:kind     :secondary
                  :size     :small
                  :disabled (or saving? (not dirty?))
                  :on-click #(>evt [::em-subs/cancel row-key])}
       (tr [:cancel])]
      [ui/button {:kind     :primary
                  :size     :small
                  :disabled (or saving? (not dirty?) (not valid?))
                  :on-click #(>evt [::em-subs/save {:tenant-id   tenant-id
                                                    :system      system
                                                    :module      (when module-dirty?
                                                                   {:enabled?     draft-enabled?
                                                                    :renewal-date (when draft-enabled? draft-date)})
                                                    :departments (when departments-dirty?
                                                                   (sort draft-departments))}])}
       (tr [:save])]]]))

;; ---------------------------------------------------------------------------
;; Table
;; ---------------------------------------------------------------------------

(defn- product-status
  "Row border colour: green when enabled, red when not."
  [enabled?]
  (if enabled? :ok :critical))

(defn- status-cell
  "Only the enabled state gets a tag; not enabled is plain text. Either way the
  state is spelled out, not left to colour."
  [tr enabled?]
  (if enabled?
    [ui-status-tag/tag {:status :ok} (tr [:admin/tenant-product-enabled])]
    [:span.text-gray-700 (tr [:admin/tenant-product-disabled])]))

(defn- product-cell [{:keys [permit-type external-module name]}]
  [:div.flex.items-center.gap-2
   (cond
     external-module
     (cc/icon {:icon        i/external-link
               :description ""
               :aria-hidden true
               :width       "16"
               :height      "16"
               :fill        "currentColor"
               :class-name  "block shrink-0"})

     (ui/permit-type-icon permit-type)
     [:> (ui/permit-type-icon permit-type) {:class-name "block shrink-0"
                                            :width      "16"
                                            :height     "16"}])
   [:span.font-medium name]])

(defn products-table [tr rows editable?]
  [sortable-table/table
   {:tr              tr
    :id              "admin-tenant-products-table"
    :items           rows
    :id-func         :id
    :initial-sorting [:name :asc]
    :get-status      (comp product-status :enabled?)
    :expanded-func   (when editable?
                       (fn [row]
                         (if (:external-module row)
                           [external-module-editor tr row]
                           [product-editor tr row])))
    :cols            [[:admin/products
                       (fn [row] [product-cell row])
                       {:sorting  (fn [a b] (compare (:name a) (:name b)))
                        :sort-key :name}]
                      [:admin/tenant-product-status
                       (fn [{:keys [enabled?]}] [status-cell tr enabled?])
                       {:sorting (fn [a b] (compare (:enabled? b) (:enabled? a)))
                        :width   "150px"}]
                      [:admin/tenant-authority-group
                       (fn [{:keys [authority-group]}] (:group/name authority-group))
                       {:sorting   (fn [a b] (compare (-> a :authority-group :group/name)
                                                      (-> b :authority-group :group/name)))
                        :truncate? true}]
                      [:admin/group-id
                       (fn [{:keys [authority-group]}] (:group/id authority-group))
                       {:sorting (fn [a b] (compare (-> a :authority-group :group/id)
                                                    (-> b :authority-group :group/id)))
                        :width   "180px"}]]}])

(defn products-tab [tr {:keys [group active?]}]
  (let [authority-groups (->> group
                              group-model/descendant-authority-groups
                              (filter group-model/application-or-upper-tier-authority?))
        ;; The category catalogue is keyed by the permit types this system
        ;; supports, so it doubles as the list of available products.
        catalogue        (<sub [::municipality-categories/permit-type->categories nil])
        rows             (into (product-rows tr {:tenant-id               (:db/id group)
                                                 :group-code              (:group/id group)
                                                 :permit-type->categories catalogue
                                                 :authority-groups        authority-groups})
                               ;; Empty with the flag off: the backend hands out
                               ;; no catalogue then.
                               (external-module-rows {:tenant-id    (:db/id group)
                                                      :group-code   (:group/id group)
                                                      :catalogue    (<sub [::em-subs/catalogue])
                                                      :domain-group group}))]
    [:section.m-0
     [:p.text-gray-600.mb-4 (tr (if active?
                                  [:admin/tenant-products-help]
                                  [:admin/tenant-inactive-read-only]))]
     (if (seq rows)
       [products-table tr rows active?]
       (cc/inline-loading {:description (tr [:loading])}))]))
